import React from "react";

const IdentityValueVisionVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson questions the most popular promotion beliefs we grow up with. It explores why effort alone plateaus, why promotions are rarely a reward for past work, and why long hours can signal the wrong thing. It reframes promotion as a future bet, not a thank-you, and starts peeling myths like tenure, certifications, job switches, and being “irreplaceable” to show what actually moves careers upward.
      </p>

    </div>
  );

  // SWAPPED: Now has the #1-#7 obvious myths transcript
  const transcriptHtml = `
    <p>Let me ask you something. Take a minute to think about it.<br/>
    What does it truly take to get promoted?</p>

    <p><strong>Ready?</strong> What came to mind?</p>

    <p>I'd say <strong>Hard work</strong>, achieving targets, long hours….?</p>

    <p>Yes, that's what all of us think. It's not wrong… but it's incomplete.<br/>
    You see, hard work is just one piece of a much larger puzzle.</p>

    <p>We've all been taught to believe that diligence alone opens doors. But in truth, it's not the hours you put in that move you up, it's what those hours build.</p>

    <p><strong>Why isn't hard work enough?</strong></p>

    <p>Think of it this way, effort without strategy can only take you so far up the ladder. That's why many reach halfway… and wonder why they can't climb further.</p>

    <p>We're here to uncover the truth behind what shapes a promotion because awareness is the first step toward change.</p>

    <p>So grab your sticky notes because we're about to challenge almost everything you've heard about promotions.</p>

    <p>We'll start with the most common ones….and work our way down to the not so common ones.</p>

    <ul style="margin-left: 20px;">
      <li>The hardest worker gets promoted</li>
      <li>Promotions are a reward for past work</li>
      <li>Longer hours prove worth</li>
      <li>Another certification will unlock it</li>
      <li>A new job will fix everything</li>
      <li>Tenure guarantees promotion</li>
      <li>Being irreplaceable helps</li>
      <li>Promotion comes after someone leaves</li>
      <li>Fixing my weaknesses is utmost important</li>
      <li>Companies don't promote in bad times</li>
      <li>My boss decides alone</li>
      <li>I don't do politics</li>
      <li>One big win is enough</li>
      <li>Complex or risky projects are key</li>
    </ul>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">#1 The hardest worker gets promoted.</h3>
    <p>Effort earns respect, not necessarily elevation.<br/>
    Many of us burn out chasing perfection, assuming output equals progress. But promotions reward impact that's visible and aligned. The people who rise don't just work harder, they make their results known, their influence felt, and their efforts strategic.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">#2 Promotions are a reward for past work</h3>
    <p>A promotion isn't a "thank you" for what you've done, <em>it's a vote of confidence in what you'll deliver next.</em></p>

    <p>Think of it like investing: your past work proves your credibility, but the decision hinges on your future potential. The question is never "What did you do?" it's "Can you sustain and scale that impact at the next level?"</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">#3 Longer hours prove worth</h3>
    <p>Long hours often signal inefficiency, not dedication. The ones who rise don't chase time, they manage impact. It's not about how much you do, but how much of it truly matters.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">#4 Another certification will unlock it.</h3>
    <p>Credentials only matter when tied to tangible business outcomes. Don't chase degrees, deliver measurable impact.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">#5 A new job will fix everything</h3>
    <p>You can switch roles, teams, even industries, but you'll still carry the same patterns with you. The rut doesn't disappear with a new desk; it disappears when you change how you show up.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">#6 Tenure guarantees promotion.</h3>
    <p>Time in a role is just one part of the journey. Organizations reward impact, not years. A junior who consistently delivers results, builds influence, and demonstrates vision can leapfrog seniors. Today, we see a lot of young CEOs chosen for their skills and ability to drive change, not their tenure. If you rely solely on "waiting your turn," you risk being overlooked, no matter how long you've been around.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">#7 Being irreplaceable helps.</h3>
    <p>Hoarding tasks may make you feel secure, but it limits your growth. True leaders create space, delegate effectively, and show they can scale their impact beyond any single role.</p>

    <p>Quite a lot isn't it? But there is more!<br/>
    Perhaps take a breather, what you would do once you break all the myths and get promoted. Also stretch your back a little and let's continue in the next video.</p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 text-body leading-7 text-[#484848] max-w-2xl"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default IdentityValueVisionVideo;

